import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { AssetVisibility } from 'src/containers/SoftwareEscrow/Technical';
import {
  PartyNotificationSettings,
  ActivityNotifications,
  DisruptedAccessNotifications,
} from 'src/components/Escrow/Notifications';
import { EscrowPartyTypeEnum } from 'src/graphql/schema';
import { SoftwareEscrowUpdateDocument } from 'src/graphql/actions/softwareEscrow/softwareEscrowUpdate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { getEscrowActivityNotificationOptions } from 'src/shared/entities/escrow-activity-notification-options';
import { disruptedNotificationsOptionsRich, gracePeriodOptions } from 'src/shared/entities/escrow-notification-options';
import { useEscrowNotificationHandlers } from 'src/containers/Escrow/Technical';

const { activityNotificationOptionsRich } = getEscrowActivityNotificationOptions('software');

const partyContentMap = {
  [EscrowPartyTypeEnum.Beneficiary]: {
    title: 'Notification settings for the beneficiary',
  },
  [EscrowPartyTypeEnum.Depositor]: {
    title: 'Choose what the beneficiary can see',
  },
};

const BeneficiaryNotificationSettings = () => {
  const {
    softwareEscrow: {
      id: escrowId,
      beneficiaryActivityNotification,
      beneficiaryDisruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod,
      currentCompanyRole,
    },
  } = useSoftwareEscrow();
  const { onActivityNotificationChange, onDisruptionNotificationChange, onGracePeriodChange } =
    useEscrowNotificationHandlers({
      escrowId,
      currentCompanyRole: currentCompanyRole!,
    });

  return (
    <PartyNotificationSettings
      className="md:grid-cols-1"
      activityNotificationNode={
        <ActivityNotifications
          description={`What type of activity notifications would you like to receive?\nThese may include details like file names and deposit timestamps.`}
          value={beneficiaryActivityNotification}
          options={activityNotificationOptionsRich}
          onChange={onActivityNotificationChange}
        />
      }
      disruptedAccessNotificationNode={
        <DisruptedAccessNotifications
          description="Choose whether to alert members if an asset becomes inaccessible or can no longer be deposited."
          graceLabel="Disruption grace period"
          graceNote="We'll keep retrying the deposit during this period before notifying you that something's wrong."
          values={{
            disruptionNotification: beneficiaryDisruptionNotification,
            disruptionNotificationGracePeriod: beneficiaryDisruptionNotificationGracePeriod ?? null,
          }}
          options={{
            disruptionNotificationOptions: disruptedNotificationsOptionsRich,
            gracePeriodOptions: gracePeriodOptions,
          }}
          onDisruptionNotificationChange={onDisruptionNotificationChange}
          onDisruptionNotificationGracePeriodChange={onGracePeriodChange}
        />
      }
    />
  );
};

const AssetVisibilityPage = () => {
  const [updateDepositSettings] = useMutation(SoftwareEscrowUpdateDocument);
  const {
    softwareEscrow: { id: softwareEscrowId, beneficiarySharedInformation, currentCompanyRole },
  } = useSoftwareEscrow();

  const onAssetVisibilityChange = async (beneficiarySharedInformation: boolean | null) => {
    if (beneficiarySharedInformation === null) return;

    try {
      const { data } = await updateDepositSettings({
        variables: {
          softwareEscrowId,
          softwareEscrowParams: {
            beneficiarySharedInformation,
          },
        },
      });

      if (data?.softwareEscrowUpdate?.success) {
        toast.success('Saved!');
      } else {
        throw new ClientError(data?.softwareEscrowUpdate?.errors?.[0]);
      }
    } catch (e: unknown) {
      getApiError(e, toast.error);
    }
  };

  const { title } = partyContentMap[currentCompanyRole!];

  const showBeneficiarySettings =
    currentCompanyRole === EscrowPartyTypeEnum.Beneficiary && beneficiarySharedInformation;

  return (
    <SoftwareOnboardingMain
      subtitle="Technical information: Beneficiary visibility"
      title={title}
      description="Note: You can edit this later under Settings → Technical Information."
    >
      <div className="max-w-260 w-full mx-auto space-y-4">
        <AssetVisibility
          onAssetVisibilityChange={onAssetVisibilityChange}
          assetVisibility={beneficiarySharedInformation}
          party={currentCompanyRole!}
        />
        {showBeneficiarySettings && <BeneficiaryNotificationSettings />}
      </div>
    </SoftwareOnboardingMain>
  );
};

export default AssetVisibilityPage;
